<template>
  <div class="custom-tag-list">
    <div class="customValues">
      <div v-if="canAddNew" class="add-new-row">
        <VTextField
          v-model="newTag"
          :placeholder="placeholder"
          appearance="ghost"
          class="vivid-input add-tag-input"
          :maxlength="120"
          @keyup.enter="addNewTag"
        ></VTextField>
        <VButton connotation="cta" appearance="filled" label="Add" @click="addNewTag" />
      </div>
      <div class="search-container" v-if="!isTagsRefreshing && tags.length > 0" ref="searchContainerRef">
        <VTextField
          v-if="!isListOpen"
          v-model="selectedTagValue"
          placeholder="Selected Value"
          appearance="ghost"
          class="vivid-input tag-searchbar"
          icon="search-line"
          @focus="openList"
          @click="openList"
        ></VTextField>
        <VTextField
          v-else
          v-model="searchTerm"
          placeholder="Search tags..."
          appearance="ghost"
          class="vivid-input tag-searchbar"
          icon="search-line"
          @focus="openList"
          @click="openList"
        ></VTextField>
        
        <div class="tags-list-container" v-if="alwaysOpen || isListOpen">
          <div class="tags-list">
            <div
              class="tag-item"
              v-for="tag in filteredTags"
              :key="tag"
              :class="{ selected: selectedTagValue === tag }"
              @click="onTagItemClick(tag)"
            >
              <span class="radio-indicator" :class="{ checked: selectedTagValue === tag }"></span>
              <span class="tag-label" :title="tag">{{ tag }}</span>
            </div>
          </div>
          
          <div v-if="filteredTags.length === 0 && searchTerm" class="no-results">
            No tags found matching "{{ searchTerm }}"
          </div>
        </div>
      </div>
      
      <div v-if="isTagsRefreshing" class="spinner-container">
        <VProgressRing connotation="cta" :size="-4" />
      </div>
      <p v-if="!isTagsRefreshing && tags.length === 0" class="no-tags">No tags available</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useCustomerTags } from '@/composables/useCustomerTags';
import { validateTag } from '@/helpers/customFields.helpers';
import { useUIStore } from '@/stores/ui';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  canAddNew: {
    type: Boolean,
    default: false
  },
  alwaysOpen: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: 'Add a new tag'
  }
});

const emit = defineEmits(['tag-selected', 'close', 'update:modelValue']);
const updateToast = (payload) => useUIStore().updateToast(payload);

// Reactive data
const { tags, tagsLoading, tagsFetching, loadTags, unsubscribe } = useCustomerTags();
const isTagsRefreshing = computed(() => tagsLoading.value || tagsFetching.value);
const selectedTagOption = ref(null);
const selectedTagValue = ref(props.modelValue || '');
const localSearchByTagText = ref('');
const searchTerm = ref('');
const newTag = ref('');
// Collapsed by default; opens on focus/click of the search box. When alwaysOpen
// is set (e.g. inside CommonSearchFilter's popover) the list stays visible.
const isListOpen = ref(false);
const searchContainerRef = ref(null);

const openList = () => {
  isListOpen.value = true;
};

const closeList = () => {
  isListOpen.value = false;
};

const onDocumentClick = (event) => {
  if (props.alwaysOpen || !isListOpen.value) return;
  if (searchContainerRef.value && !searchContainerRef.value.contains(event.target)) {
    closeList();
  }
};

// Computed properties
const filteredTags = computed(() => {
  if (!searchTerm.value) {
    return tags.value;
  }
  return tags.value.filter(tag => 
    tag.toLowerCase().includes(searchTerm.value.toLowerCase())
  );
});

// Methods
const fetchTags = async () => {
  if (tagsLoading.value || tags.value.length > 0) {
    return;
  }
  try {
    await loadTags();
  } catch (error) {
    console.error('Failed to load tags:', error);
  }
};

const selectTag = (tag) => {
  selectedTagOption.value = {
    label: tag,
    value: tag,
    title: tag
  };
  selectedTagValue.value = tag;
  localSearchByTagText.value = tag;
};

const onTagItemClick = (tag) => {
  selectedTagValue.value = tag;
  selectTag(tag);
  emit('update:modelValue', tag);
  searchBySelectedTag();
  if (!props.alwaysOpen) closeList();
};

const addNewTag = () => {
  const trimmedTag = newTag.value?.trim();
  const validationError = validateTag(trimmedTag, tags.value.map(tag => ({ tag })));
  if (validationError) {
    updateToast({ open: true, message: validationError, type: 'danger' });
    return;
  }
  newTag.value = '';
  onTagItemClick(trimmedTag);
};

const searchBySelectedTag = () => {
  if (!localSearchByTagText.value) {
    emit('tag-selected', {
      action: 'error',
      message: 'Please select a tag'
    });
    return;
  }
  emit('tag-selected', {
    action: 'searchRequested',
    value: localSearchByTagText.value,
    selectedOption: selectedTagOption.value
  });
};

watch(
  () => props.modelValue,
  (value) => {
    selectedTagValue.value = value || '';
  }
);

// Lifecycle
onMounted(async () => {
  await fetchTags();
  document.addEventListener('click', onDocumentClick, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick, true);
  if (unsubscribe) unsubscribe();
});
</script>

<style lang="scss" scoped>
.custom-tag-list {
  width: 100%;
}

.customValues {
  padding: 10px;
}

.add-new-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.add-tag-input {
  flex: 1;
}

.tag-searchbar {
  margin-bottom: 10px;
}

.tags-list-container {
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: var(--background-primary);
  }
  &::-webkit-scrollbar-thumb {
    background: var(--gray-04);
    border-radius: 2px;
    &:hover {
      background: var(--black);
    }
  }
}

.tags-list {
  margin: 0;

  .tag-item {
    display: flex;
    align-items: center;
    padding: 12px 15px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    border-bottom: 1px solid var(--background-primary);
    gap: 12px;
    min-width: 0;
    overflow: hidden;

    &:last-child {
      border-bottom: none;
    }
    &:hover {
      background-color: var(--purple-opac);
    }
    &.selected {
      background-color: var(--purple-opac);
    }

    .tag-label {
      font-size: 14px;
      color: var(--black);
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.radio-indicator {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid var(--gray-04);
  flex-shrink: 0;
  position: relative;

  &.checked {
    border-color: var(--purple);
    &::after {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: var(--purple);
    }
  }
}

.no-tags,
.no-results {
  text-align: center;
  color: var(--gray-04);
  font-style: italic;
  padding: 20px;
}

.no-results {
  padding: 15px;
  font-size: 14px;
}

.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
  min-height: 100px;
}
</style>
